/**
 * Device table: DataTable for the main list; optional row reorder for house devices.
 */
import { useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { toast } from "react-hot-toast";
import {
  Ban,
  HardDrive,
  Plus,
  Home,
  CreditCard,
  KeyRound,
  MoreHorizontal,
  CheckCircle2,
  Info,
  Link2Off,
  PencilLine,
  Save,
  StickyNote,
  Users,
  Volume,
  Volume1,
  Volume2,
  Wifi,
  WifiHigh,
  WifiLow,
  MemoryStick,
  X,
  type LucideIcon,
} from "lucide-react";
import { CopyableSerialNumber } from "@/components/device/CopyableSerialNumber";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  HoverPopover,
  Input,
  fieldControlClass,
  Modal,
  ModalFooter,
  Switch,
  Textarea,
} from "@/components/ui";
import {
  DataTable,
  createActionsColumn,
  type DataTableColumn,
  type DataTableLoadState,
  type DataTableSelectionBulkAction,
} from "@/components/table";
import {
  DeviceSetupBadge,
  DeviceTelemetryDot,
} from "@/components/device/DeviceStatusIndicators";
import {
  formatDeviceAppVersion,
  getDeviceStatusData,
  getDeviceStatusTimestamp,
  getBatteryIcon,
  readTelemetryBoolean,
  readTelemetryNumber,
  TELEMETRY_FRESH_MS,
  type DeviceStatusPayload,
} from "@/lib/deviceStatus";
import { isReaderPaired } from "@/lib/readerLabel";
import { readerSerialSuffix } from "@/lib/readerSerialNumber";
import { cn } from "@/lib/utils";
import { formatDateTime, formatRelativeTime } from "@/lib/datetime";
import type { Device } from "@/types";

export type DeviceTableSortColumn =
  | "device"
  | "serial"
  | "googleAccount"
  | "unlock"
  | "reader"
  | "house"
  | "appVersion"
  | "telemetry"
  | "interaction";

const CELL_TRUNCATE_CLASS = "block min-w-0 truncate";
/** Inline edit affordance: quiet until the cell is hovered or focused. */
const HOVER_REVEAL_CLASS =
  "h-6 w-6 shrink-0 text-surface-400 opacity-0 transition-opacity group-hover/cell:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100";

export type DeviceTableRow = Device;

export interface DevicesTableLabels {
  colDevice: string;
  colSerial: string;
  colGoogleAccount: string;
  colFreekioskUnlock: string;
  colReader: string;
  colHouse?: string;
  colLastTelemetry: string;
  colLastInteraction: string;
  colAppVersion: string;
  colInUse: string;
  colActions: string;
  emptyDefault: string;
  readerPair: string;
  readerUnpairTitle: string;
  assign?: string;
  houseRemoveTitle?: string;
  houseOpenTitle?: string;
  lastTelemetryNever: string;
  valueAppVersionUnknown: string;
  valueAppVersionLegacy: string;
  valueFreekioskUnlockUnavailable: string;
  moreActionsTitle: string;
  menuRegenerateCredentials: string;
  menuRevokeDevice: string;
  menuReactivateDevice: string;
  menuManagePresences?: string;
  menuOtherInfo: string;
  menuNotes: string;
  unassignDeviceTitle?: string;
  renameDeviceTitle?: string;
  displayNamePlaceholder?: string;
  displayNameEmpty?: string;
  editGoogleAccountTitle?: string;
  editNotesTitle: string;
  notesPlaceholder: string;
  telemetryBattery: string;
  telemetryWifi: string;
  telemetryAudio: string;
  telemetryStorage: string;
  telemetryMemory: string;
  telemetryUnavailable: string;
  telemetryDataAge: (age: string) => string;
}

export interface DevicesTableProps {
  devices: DeviceTableRow[];
  labels: DevicesTableLabels;
  /** Persist column prefs / page size. */
  preferencesKey: string;
  showHouseColumn?: boolean;
  /** Extra columns that start hidden in this context (still toggleable in preferences). */
  defaultHiddenColumns?: readonly DeviceTableSortColumn[];
  emptyMessage?: string;
  selectedIds?: ReadonlySet<string>;
  onSelectedIdsChange?: (next: Set<string>) => void;
  selectionBulkAction?: DataTableSelectionBulkAction;
  /** Heading on the left of the table's header row. */
  title?: ReactNode;
  /** Controls on the right of the header row (search, primary action…). */
  actions?: ReactNode;
  toolbar?: ReactNode;
  loadState?: DataTableLoadState;
  resetToken?: string | number;
  /** When set, rows can be drag-reordered (house device lists). */
  onReorder?: (reordered: DeviceTableRow[]) => void;
  reorderDisabled?: boolean;
  dragHandleLabel?: string;
  onPairReader: (device: DeviceTableRow) => void;
  onUnpairReader: (device: DeviceTableRow) => void;
  onAssignHouse?: (device: DeviceTableRow) => void;
  onRemoveHouse?: (device: DeviceTableRow) => void;
  onOpenHouse?: (houseId: string) => void;
  onUnassignDevice?: (device: DeviceTableRow) => void;
  onRegenerateCredentials: (device: DeviceTableRow) => void;
  onToggleActive: (device: DeviceTableRow) => void;
  onToggleShelved: (device: DeviceTableRow) => void;
  onShowOtherInfo: (device: DeviceTableRow) => void;
  onManagePresences?: (device: DeviceTableRow) => void;
  onUpdateDisplayName: (
    device: DeviceTableRow,
    displayName: string,
  ) => Promise<void>;
  onUpdateGoogleAccount: (
    device: DeviceTableRow,
    googleAccount: string,
  ) => Promise<void>;
  onUpdateNotes: (device: DeviceTableRow, notes: string | null) => Promise<void>;
}

type TelemetryMetric = {
  label: string;
  value: string;
  icon: LucideIcon;
  colorClassName?: string;
};

const TELEMETRY_AMBER_CLASS = "text-warning-600";
const TELEMETRY_GREEN_CLASS = "text-success-600";
const TELEMETRY_RED_CLASS = "text-error-600";

function getWifiIcon(signalLevel: number | null): LucideIcon {
  if (signalLevel === null || signalLevel > 70) {
    return Wifi;
  }
  if (signalLevel > 40) {
    return WifiHigh;
  }
  return WifiLow;
}

function getAudioIcon(volume: number | null): LucideIcon {
  if (volume === null || volume > 70) {
    return Volume2;
  }
  if (volume > 40) {
    return Volume1;
  }
  return Volume;
}

function getUsageColorClass(usedPercent: number | null): string | undefined {
  if (usedPercent !== null && usedPercent > 90) {
    return TELEMETRY_RED_CLASS;
  }
  if (usedPercent !== null && usedPercent > 70) {
    return TELEMETRY_AMBER_CLASS;
  }
  return undefined;
}

function isStaleTelemetryTimestamp(timestamp: Date): boolean {
  return Date.now() - timestamp.getTime() > TELEMETRY_FRESH_MS;
}

function formatPercentValue(value: number | null, unavailableLabel: string) {
  return value === null ? unavailableLabel : `${Math.round(value)}%`;
}

function hasDisplayableTelemetry(
  payload: DeviceStatusPayload | null | undefined,
): boolean {
  const statusData = getDeviceStatusData(payload);
  return (
    getDeviceStatusTimestamp(payload) !== null ||
    readTelemetryNumber(statusData, "battery", "level") !== null ||
    readTelemetryBoolean(statusData, "battery", "charging") !== null ||
    readTelemetryNumber(statusData, "wifi", "signalLevel") !== null ||
    readTelemetryNumber(statusData, "audio", "volume") !== null ||
    readTelemetryNumber(statusData, "storage", "usedPercent") !== null ||
    readTelemetryNumber(statusData, "memory", "usedPercent") !== null
  );
}

function getTelemetryMetrics(
  device: DeviceTableRow,
  labels: DevicesTableLabels,
): TelemetryMetric[] {
  const statusData = getDeviceStatusData(device.device_status_payload);
  const batteryLevel = readTelemetryNumber(statusData, "battery", "level");
  const batteryCharging = readTelemetryBoolean(
    statusData,
    "battery",
    "charging",
  );
  const wifiSignalLevel = readTelemetryNumber(
    statusData,
    "wifi",
    "signalLevel",
  );
  const audioVolume = readTelemetryNumber(statusData, "audio", "volume");
  const storageUsedPercent = readTelemetryNumber(
    statusData,
    "storage",
    "usedPercent",
  );
  const memoryUsedPercent = readTelemetryNumber(
    statusData,
    "memory",
    "usedPercent",
  );

  return [
    {
      label: labels.telemetryBattery,
      value: formatPercentValue(batteryLevel, labels.telemetryUnavailable),
      icon: getBatteryIcon(batteryLevel, batteryCharging),
      colorClassName:
        batteryCharging === true
          ? TELEMETRY_GREEN_CLASS
          : batteryLevel !== null && batteryLevel < 15
            ? TELEMETRY_RED_CLASS
            : undefined,
    },
    {
      label: labels.telemetryWifi,
      value: formatPercentValue(wifiSignalLevel, labels.telemetryUnavailable),
      icon: getWifiIcon(wifiSignalLevel),
      colorClassName:
        wifiSignalLevel !== null && wifiSignalLevel < 10
          ? TELEMETRY_RED_CLASS
          : undefined,
    },
    {
      label: labels.telemetryAudio,
      value: formatPercentValue(audioVolume, labels.telemetryUnavailable),
      icon: getAudioIcon(audioVolume),
      colorClassName:
        audioVolume !== null && audioVolume < 5
          ? TELEMETRY_AMBER_CLASS
          : undefined,
    },
    {
      label: labels.telemetryStorage,
      value: formatPercentValue(
        storageUsedPercent,
        labels.telemetryUnavailable,
      ),
      icon: HardDrive,
      colorClassName: getUsageColorClass(storageUsedPercent),
    },
    {
      label: labels.telemetryMemory,
      value: formatPercentValue(memoryUsedPercent, labels.telemetryUnavailable),
      icon: MemoryStick,
      colorClassName: getUsageColorClass(memoryUsedPercent),
    },
  ];
}

function DeviceTelemetrySummary({
  device,
  labels,
}: {
  device: DeviceTableRow;
  labels: DevicesTableLabels;
}) {
  const metrics = getTelemetryMetrics(device, labels);
  const telemetryTimestamp = getDeviceStatusTimestamp(
    device.device_status_payload,
  );
  const isTelemetryTimestampStale =
    telemetryTimestamp !== null &&
    isStaleTelemetryTimestamp(telemetryTimestamp);

  return (
    <div className="space-y-2">
      <div className="grid max-w-[calc(100vw-2rem)] grid-cols-[max-content_max-content] gap-x-3 gap-y-2">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.label}
              title={metric.label}
              className={cn(
                "flex min-w-0 items-center gap-1.5 text-sm font-semibold leading-none",
                metric.colorClassName ?? "text-surface-900",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0",
                  metric.colorClassName ? "text-current" : "text-primary-600",
                )}
              />
              <span className="break-words">{metric.value}</span>
            </div>
          );
        })}
      </div>
      {telemetryTimestamp && (
        <p
          className={cn(
            "text-[11px] leading-none",
            isTelemetryTimestampStale ? "text-error-600" : "text-surface-500",
          )}
        >
          {labels.telemetryDataAge(formatRelativeTime(telemetryTimestamp))}
        </p>
      )}
    </div>
  );
}

type DeviceCellHandlers = {
  labels: DevicesTableLabels;
  editingDisplayNameDeviceId: string | null;
  displayNameDrafts: Record<string, string>;
  savingDisplayNameDeviceId: string | null;
  onPairReader: (device: DeviceTableRow) => void;
  onUnpairReader: (device: DeviceTableRow) => void;
  onAssignHouse?: (device: DeviceTableRow) => void;
  onRemoveHouse?: (device: DeviceTableRow) => void;
  onOpenHouse?: (houseId: string) => void;
  onUnassignDevice?: (device: DeviceTableRow) => void;
  onRegenerateCredentials: (device: DeviceTableRow) => void;
  onToggleActive: (device: DeviceTableRow) => void;
  onToggleShelved: (device: DeviceTableRow) => void;
  onShowOtherInfo: (device: DeviceTableRow) => void;
  onManagePresences?: (device: DeviceTableRow) => void;
  onUpdateDisplayName: (
    device: DeviceTableRow,
    displayName: string,
  ) => Promise<void>;
  onUpdateGoogleAccount: (
    device: DeviceTableRow,
    googleAccount: string,
  ) => Promise<void>;
  getDisplayNameDraft: (device: DeviceTableRow) => string;
  handleStartDisplayNameEdit: (device: DeviceTableRow) => void;
  handleCancelDisplayNameEdit: (deviceId: string) => void;
  handleSaveDisplayName: (device: DeviceTableRow) => Promise<void>;
  setDisplayNameDrafts: Dispatch<SetStateAction<Record<string, string>>>;
  handleEditGoogleAccount: (device: DeviceTableRow) => void;
  handleEditNotes: (device: DeviceTableRow) => void;
};

function renderDeviceCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const hasTelemetry = hasDisplayableTelemetry(device.device_status_payload);
  const notes = device.notes?.trim() ?? "";

  return (
    <div
      id={`device-row-${device.id}`}
      className="group/cell flex min-w-0 items-center gap-2"
    >
      <DeviceTelemetryDot device={device} />
      {h.editingDisplayNameDeviceId === device.id ? (
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <input
            type="text"
            value={h.getDisplayNameDraft(device)}
            onChange={(event) =>
              h.setDisplayNameDrafts((current) => ({
                ...current,
                [device.id]: event.target.value,
              }))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void h.handleSaveDisplayName(device);
              }
              if (event.key === "Escape") {
                h.handleCancelDisplayNameEdit(device.id);
              }
            }}
            placeholder={h.labels.displayNamePlaceholder}
            disabled={h.savingDisplayNameDeviceId === device.id}
            className={fieldControlClass({ size: "sm", className: "min-w-0 flex-1" })}
            autoFocus
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => void h.handleSaveDisplayName(device)}
            disabled={h.savingDisplayNameDeviceId === device.id}
            aria-label={h.labels.renameDeviceTitle}
          >
            <Save className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => h.handleCancelDisplayNameEdit(device.id)}
            disabled={h.savingDisplayNameDeviceId === device.id}
            aria-label={h.labels.renameDeviceTitle}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-0.5">
            {hasTelemetry ? (
              <HoverPopover
                content={
                  <DeviceTelemetrySummary device={device} labels={h.labels} />
                }
                placement="below"
                contentClassName="rounded-lg border border-surface-200 bg-surface-0 p-2.5 text-surface-900 shadow-popover"
              >
                <span
                  className="block min-w-0 cursor-default truncate text-sm font-medium text-surface-900 underline decoration-surface-300 decoration-dotted underline-offset-4"
                  title={device.display_name}
                >
                  {device.display_name}
                </span>
              </HoverPopover>
            ) : (
              <span
                className="block min-w-0 truncate text-sm font-medium text-surface-900"
                title={device.display_name}
              >
                {device.display_name}
              </span>
            )}
            <DeviceSetupBadge device={device} />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={cn(HOVER_REVEAL_CLASS, "ml-auto")}
              onClick={() => h.handleStartDisplayNameEdit(device)}
              aria-label={h.labels.renameDeviceTitle}
            >
              <PencilLine className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-surface-500">
            <span className="shrink-0 font-mono text-[11px] tracking-tight">
              {device.kiosk_code}
            </span>
            {notes ? (
              <>
                <span className="text-surface-300" aria-hidden>
                  ·
                </span>
                <span className="min-w-0 truncate" title={notes}>
                  {notes}
                </span>
              </>
            ) : null}
          </p>
        </div>
      )}
    </div>
  );
}

function renderSerialCell(device: DeviceTableRow) {
  return (
    <CopyableSerialNumber
      value={device.serial_number}
      compactSuffixLength={5}
    />
  );
}

function renderGoogleAccountCell(
  device: DeviceTableRow,
  h: DeviceCellHandlers,
) {
  return (
    <div className="group/cell flex min-w-0 items-center gap-0.5">
      <span className={CELL_TRUNCATE_CLASS} title={device.google_account}>
        {device.google_account}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={cn(HOVER_REVEAL_CLASS, "ml-auto")}
        onClick={() => h.handleEditGoogleAccount(device)}
        aria-label={h.labels.editGoogleAccountTitle}
      >
        <PencilLine className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function renderUnlockCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return device.freekiosk_unlock_code ? (
    <ClickToCopyId
      value={device.freekiosk_unlock_code}
      className="text-[13px]"
    />
  ) : (
    <span className="text-surface-400">
      {h.labels.valueFreekioskUnlockUnavailable}
    </span>
  );
}

function renderReaderCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const reader = device.reader;
  if (!reader || !isReaderPaired(reader)) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="-ml-2 text-primary-700 hover:bg-primary-50 hover:text-primary-800"
        leftIcon={<CreditCard className="h-3.5 w-3.5" />}
        onClick={() => h.onPairReader(device)}
      >
        {h.labels.readerPair}
      </Button>
    );
  }

  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      <ClickToCopyId
        value={reader.serial_number}
        displayValue={readerSerialSuffix(reader.serial_number)}
        ellipsis="start"
        className="text-[13px]"
      />
      <button
        type="button"
        onClick={() => h.onUnpairReader(device)}
        title={h.labels.readerUnpairTitle}
        aria-label={`${h.labels.readerUnpairTitle} (${reader.serial_number})`}
        className="group inline-flex shrink-0 items-center rounded-md p-0.5 leading-none text-success-600 transition hover:bg-error-50 hover:text-error-700"
      >
        <CheckCircle2 className="h-4 w-4 group-hover:hidden" />
        <Link2Off className="hidden h-4 w-4 group-hover:block" />
      </button>
    </span>
  );
}

function renderHouseCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const house = device.house;
  if (house) {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            h.onRemoveHouse?.(device);
          }}
          title={h.labels.houseRemoveTitle}
          aria-label={h.labels.houseRemoveTitle}
          className="group inline-flex shrink-0 items-center rounded-md p-0.5 leading-none text-surface-400 transition hover:bg-error-50 hover:text-error-700"
        >
          <Home className="h-4 w-4 group-hover:hidden" />
          <Link2Off className="hidden h-4 w-4 group-hover:block" />
        </button>
        <button
          type="button"
          onClick={() => h.onOpenHouse?.(house.id)}
          title={h.labels.houseOpenTitle}
          className="block min-w-0 truncate text-left text-sm text-primary-700 hover:text-primary-900 hover:underline"
        >
          {house.name}
        </button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      className="-ml-2 text-primary-700 hover:bg-primary-50 hover:text-primary-800"
      leftIcon={<Plus className="h-3.5 w-3.5" />}
      onClick={() => h.onAssignHouse?.(device)}
    >
      {h.labels.assign}
    </Button>
  );
}

function renderAppVersionCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  const appVersion = formatDeviceAppVersion(device, {
    unknown: h.labels.valueAppVersionUnknown,
    legacy: h.labels.valueAppVersionLegacy,
  });
  const isUnknown = appVersion === h.labels.valueAppVersionUnknown;
  return (
    <span
      className={cn(
        CELL_TRUNCATE_CLASS,
        isUnknown ? "text-surface-400" : "font-mono text-[13px] text-surface-700",
      )}
    >
      {appVersion}
    </span>
  );
}

function renderRelativeTimestampCell(
  iso: string | null | undefined,
  neverLabel: string,
) {
  if (iso) {
    return (
      <HoverPopover content={formatDateTime(iso)} placement="above">
        <span className="cursor-default whitespace-nowrap tabular-nums">
          {formatRelativeTime(iso)}
        </span>
      </HoverPopover>
    );
  }
  return (
    <span className="whitespace-nowrap text-surface-400">{neverLabel}</span>
  );
}

function renderTelemetryCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return renderRelativeTimestampCell(
    device.last_device_report_at,
    h.labels.lastTelemetryNever,
  );
}

function renderInteractionCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return renderRelativeTimestampCell(
    device.last_interaction_at,
    h.labels.lastTelemetryNever,
  );
}

function renderInUseCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return (
    <Switch
      checked={device.is_shelved !== true}
      onChange={() => h.onToggleShelved(device)}
      aria-label={h.labels.colInUse}
    />
  );
}

function renderActionsCell(device: DeviceTableRow, h: DeviceCellHandlers) {
  return (
    <div className="relative flex items-center justify-end gap-1">
      {h.onUnassignDevice && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800"
          aria-label={h.labels.unassignDeviceTitle}
          onClick={() => h.onUnassignDevice?.(device)}
        >
          <Link2Off className="h-4 w-4" />
        </Button>
      )}
      <ActionMenu
        renderTrigger={(triggerProps) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={h.labels.moreActionsTitle}
            {...triggerProps}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      >
        <ActionMenuItem
          icon={KeyRound}
          onSelect={() => h.onRegenerateCredentials(device)}
        >
          {h.labels.menuRegenerateCredentials}
        </ActionMenuItem>
        <ActionMenuItem
          icon={device.is_revoked ? CheckCircle2 : Ban}
          onSelect={() => h.onToggleActive(device)}
        >
          {device.is_revoked
            ? h.labels.menuReactivateDevice
            : h.labels.menuRevokeDevice}
        </ActionMenuItem>
        {h.onManagePresences && h.labels.menuManagePresences ? (
          <ActionMenuItem
            icon={Users}
            onSelect={() => h.onManagePresences?.(device)}
          >
            {h.labels.menuManagePresences}
          </ActionMenuItem>
        ) : null}
        <ActionMenuItem
          icon={StickyNote}
          onSelect={() => h.handleEditNotes(device)}
        >
          {h.labels.menuNotes}
        </ActionMenuItem>
        <ActionMenuItem
          icon={Info}
          onSelect={() => h.onShowOtherInfo(device)}
        >
          {h.labels.menuOtherInfo}
        </ActionMenuItem>
      </ActionMenu>
    </div>
  );
}

export function DevicesTable({
  devices,
  labels,
  preferencesKey,
  showHouseColumn = false,
  defaultHiddenColumns,
  emptyMessage,
  selectedIds,
  onSelectedIdsChange,
  selectionBulkAction,
  title,
  actions,
  toolbar,
  loadState,
  resetToken,
  onReorder,
  reorderDisabled = false,
  dragHandleLabel,
  onPairReader,
  onUnpairReader,
  onAssignHouse,
  onRemoveHouse,
  onOpenHouse,
  onUnassignDevice,
  onRegenerateCredentials,
  onToggleActive,
  onToggleShelved,
  onShowOtherInfo,
  onManagePresences,
  onUpdateDisplayName,
  onUpdateGoogleAccount,
  onUpdateNotes,
}: DevicesTableProps) {
  const { t } = useTranslation("common");
  const { t: tDevices } = useTranslation("devices");
  const tableLabels = buildDataTableLabels(t);
  const [editingDisplayNameDeviceId, setEditingDisplayNameDeviceId] = useState<
    string | null
  >(null);
  const [displayNameDrafts, setDisplayNameDrafts] = useState<
    Record<string, string>
  >({});
  const [savingDisplayNameDeviceId, setSavingDisplayNameDeviceId] = useState<
    string | null
  >(null);
  const [editingGoogleAccountDevice, setEditingGoogleAccountDevice] =
    useState<DeviceTableRow | null>(null);
  const [googleAccountDraft, setGoogleAccountDraft] = useState("");
  const [savingGoogleAccount, setSavingGoogleAccount] = useState(false);
  const [editingNotesDevice, setEditingNotesDevice] =
    useState<DeviceTableRow | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const getDisplayNameDraft = (device: DeviceTableRow): string =>
    displayNameDrafts[device.id] ?? device.display_name;

  const handleStartDisplayNameEdit = (device: DeviceTableRow) => {
    setEditingDisplayNameDeviceId(device.id);
    setDisplayNameDrafts((current) => ({
      ...current,
      [device.id]: device.display_name,
    }));
  };

  const handleCancelDisplayNameEdit = (deviceId: string) => {
    setEditingDisplayNameDeviceId(null);
    setDisplayNameDrafts((current) => {
      const next = { ...current };
      delete next[deviceId];
      return next;
    });
  };

  const handleSaveDisplayName = async (device: DeviceTableRow) => {
    const nextName = getDisplayNameDraft(device).trim();
    if (!nextName) {
      if (labels.displayNameEmpty) {
        toast.error(labels.displayNameEmpty);
      }
      return;
    }
    if (nextName === device.display_name) {
      handleCancelDisplayNameEdit(device.id);
      return;
    }

    try {
      setSavingDisplayNameDeviceId(device.id);
      await onUpdateDisplayName(device, nextName);
      handleCancelDisplayNameEdit(device.id);
    } finally {
      setSavingDisplayNameDeviceId(null);
    }
  };

  const handleEditGoogleAccount = (device: DeviceTableRow) => {
    setEditingGoogleAccountDevice(device);
    setGoogleAccountDraft(device.google_account);
  };

  const handleEditNotes = (device: DeviceTableRow) => {
    setEditingNotesDevice(device);
    setNotesDraft(device.notes ?? "");
  };

  const handleCloseNotesModal = () => {
    if (savingNotes) return;
    setEditingNotesDevice(null);
    setNotesDraft("");
  };

  const handleSaveNotes = async () => {
    if (!editingNotesDevice) return;
    const nextNotes = notesDraft.trim() || null;
    const currentNotes = editingNotesDevice.notes?.trim() || null;
    if (nextNotes === currentNotes) {
      handleCloseNotesModal();
      return;
    }
    try {
      setSavingNotes(true);
      await onUpdateNotes(editingNotesDevice, nextNotes);
      setEditingNotesDevice(null);
      setNotesDraft("");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCloseGoogleAccountModal = () => {
    if (savingGoogleAccount) return;
    setEditingGoogleAccountDevice(null);
    setGoogleAccountDraft("");
  };

  const handleSaveGoogleAccount = async () => {
    if (!editingGoogleAccountDevice) return;
    const trimmed = googleAccountDraft.trim();
    if (!trimmed || trimmed === editingGoogleAccountDevice.google_account) {
      handleCloseGoogleAccountModal();
      return;
    }
    try {
      setSavingGoogleAccount(true);
      await onUpdateGoogleAccount(editingGoogleAccountDevice, trimmed);
      setEditingGoogleAccountDevice(null);
      setGoogleAccountDraft("");
    } finally {
      setSavingGoogleAccount(false);
    }
  };

  const cellHandlers: DeviceCellHandlers = {
    labels,
    editingDisplayNameDeviceId,
    displayNameDrafts,
    savingDisplayNameDeviceId,
    onPairReader,
    onUnpairReader,
    onAssignHouse,
    onRemoveHouse,
    onOpenHouse,
    onUnassignDevice,
    onRegenerateCredentials,
    onToggleActive,
    onToggleShelved,
    onShowOtherInfo,
    onManagePresences,
    onUpdateDisplayName,
    onUpdateGoogleAccount,
    getDisplayNameDraft,
    handleStartDisplayNameEdit,
    handleCancelDisplayNameEdit,
    handleSaveDisplayName,
    setDisplayNameDrafts,
    handleEditGoogleAccount,
    handleEditNotes,
  };
  const cellHandlersRef = useRef(cellHandlers);
  cellHandlersRef.current = cellHandlers;

  const columns = useMemo((): DataTableColumn<
    DeviceTableRow,
    DeviceTableSortColumn
  >[] => {
    const withHandlers =
      (render: (device: DeviceTableRow, h: DeviceCellHandlers) => ReactNode) =>
      (device: DeviceTableRow) =>
        render(device, cellHandlersRef.current);

    const cols: DataTableColumn<DeviceTableRow, DeviceTableSortColumn>[] = [
      {
        id: "device",
        header: labels.colDevice,
        sortKey: "device",
        sortKind: "text",
        getSortValue: (device) => device.display_name,
        defaultWidth: 240,
        cell: withHandlers(renderDeviceCell),
      },
      {
        id: "serial",
        header: labels.colSerial,
        sortKey: "serial",
        sortKind: "text",
        getSortValue: (device) => device.serial_number,
        defaultWidth: 140,
        cell: (device) => renderSerialCell(device),
      },
      {
        id: "googleAccount",
        defaultHidden: true,
        header: labels.colGoogleAccount,
        sortKey: "googleAccount",
        sortKind: "text",
        getSortValue: (device) => device.google_account,
        defaultWidth: 180,
        cell: withHandlers(renderGoogleAccountCell),
      },
      {
        id: "unlock",
        defaultHidden: true,
        header: labels.colFreekioskUnlock,
        sortKey: "unlock",
        sortKind: "text",
        getSortValue: (device) => device.freekiosk_unlock_code,
        defaultWidth: 140,
        cell: withHandlers(renderUnlockCell),
      },
      {
        id: "reader",
        header: labels.colReader,
        sortKey: "reader",
        sortKind: "text",
        getSortValue: (device) => device.reader?.serial_number,
        defaultWidth: 160,
        cell: withHandlers(renderReaderCell),
      },
    ];

    if (showHouseColumn && labels.colHouse) {
      cols.push({
        id: "house",
        header: labels.colHouse,
        sortKey: "house",
        sortKind: "text",
        getSortValue: (device) => device.house?.name,
        defaultWidth: 160,
        cell: withHandlers(renderHouseCell),
      });
    }

    cols.push(
      {
        id: "appVersion",
        header: labels.colAppVersion,
        sortKey: "appVersion",
        sortKind: "text",
        getSortValue: (device) => {
          const version = formatDeviceAppVersion(device, {
            unknown: labels.valueAppVersionUnknown,
            legacy: labels.valueAppVersionLegacy,
          });
          if (
            version === labels.valueAppVersionUnknown ||
            version === labels.valueAppVersionLegacy
          ) {
            return null;
          }
          return version;
        },
        defaultWidth: 120,
        cell: withHandlers(renderAppVersionCell),
      },
      {
        id: "telemetry",
        header: labels.colLastTelemetry,
        sortKey: "telemetry",
        sortKind: "date",
        getSortValue: (device) =>
          device.last_device_report_at
            ? Date.parse(device.last_device_report_at)
            : null,
        defaultWidth: 140,
        cell: withHandlers(renderTelemetryCell),
      },
      {
        id: "interaction",
        header: labels.colLastInteraction,
        sortKey: "interaction",
        sortKind: "date",
        getSortValue: (device) =>
          device.last_interaction_at
            ? Date.parse(device.last_interaction_at)
            : null,
        defaultWidth: 140,
        cell: withHandlers(renderInteractionCell),
      },
      {
        id: "inUse",
        header: labels.colInUse,
        defaultWidth: 120,
        cell: withHandlers(renderInUseCell),
      },
      createActionsColumn(labels.colActions, {
        cellClassName: "px-3 text-right font-medium",
        cell: withHandlers(renderActionsCell),
      }),
    );

    if (!defaultHiddenColumns) {
      return cols;
    }
    return cols.map((column) =>
      column.sortKey && defaultHiddenColumns.includes(column.sortKey)
        ? { ...column, defaultHidden: true }
        : column,
    );
  }, [defaultHiddenColumns, labels, showHouseColumn]);

  return (
    <>
      <DataTable<DeviceTableRow, DeviceTableSortColumn>
        labels={tableLabels}
        closeLabel={t("close")}
        loadingLabel={t("loading")}
        rows={devices}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="device"
        preferencesKey={preferencesKey}
        paginate={!onReorder}
        resetToken={resetToken}
        loadState={loadState}
        emptyMessage={emptyMessage ?? labels.emptyDefault}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
        selectionBulkAction={selectionBulkAction}
        selectionRowAriaLabel={(device) =>
          tDevices("page.selectRow", { name: device.display_name })
        }
        title={title}
        actions={actions}
        toolbar={toolbar}
        reorder={
          onReorder && dragHandleLabel
            ? {
                onReorder,
                disabled: reorderDisabled,
                dragHandleLabel,
              }
            : undefined
        }
        rowClassName={(row) =>
          cn(
            "transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50",
            selectedIds?.has(row.id) && "bg-primary-50/40 dark:bg-primary-50/60",
          )
        }
      />

      <Modal
        isOpen={editingGoogleAccountDevice != null}
        onClose={handleCloseGoogleAccountModal}
        closeLabel={t("close")}
        title={labels.editGoogleAccountTitle ?? labels.colGoogleAccount}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              variant="outline"
              onClick={handleCloseGoogleAccountModal}
              disabled={savingGoogleAccount}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => void handleSaveGoogleAccount()}
              isLoading={savingGoogleAccount}
              disabled={!googleAccountDraft.trim()}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        }
      >
        <Input
          value={googleAccountDraft}
          onChange={(event) => setGoogleAccountDraft(event.target.value)}
          placeholder={labels.colGoogleAccount}
          autoFocus
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleSaveGoogleAccount();
            }
          }}
        />
      </Modal>

      <Modal
        isOpen={editingNotesDevice != null}
        onClose={handleCloseNotesModal}
        closeLabel={t("close")}
        title={labels.editNotesTitle}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              variant="outline"
              onClick={handleCloseNotesModal}
              disabled={savingNotes}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => void handleSaveNotes()}
              isLoading={savingNotes}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        }
      >
        <Textarea
          value={notesDraft}
          onChange={(event) => setNotesDraft(event.target.value)}
          placeholder={labels.notesPlaceholder}
          rows={4}
          autoFocus
        />
      </Modal>
    </>
  );
}
